import { useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { Button, Select } from '@nyce/admin-kit/ui';
import { SCOPES } from '../../api';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
} from '../../components/common';
import { stats, type ErrorGroup, type StatusFilter } from '../../api/stats';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useHubLogin } from '../../session';
import { Sparkline } from '@nyce/admin-kit/console';
import { useBulk } from './Incidents';
import { MuteGroupDialog } from './MuteGroup';
import {
  EntryStatusChip,
  OrgLink,
  RangeTabs,
  fmtInt,
  rangeWindow,
  severityLabel,
  statsError,
  useParam,
  useRange,
} from './shared';
import './statistics.css';

/** One occurrence's full payload (call stacks, client, user), opened under its row. */
function OccurrenceDetail({ id }: { id: string }) {
  const detail = useAsync(() => stats.error(id), [id]);
  return (
    <div className="sx-occurrence" data-testid="occurrence-detail">
      <ErrorBox error={detail.error} />
      <Loading loading={detail.loading} />
      {detail.data && (
        <>
          <dl className="sx-meta">
            <span>
              <b>{detail.data.entry.name ?? detail.data.entry.customerId}</b> ·{' '}
              {detail.data.entry.workserver ?? '—'}
            </span>
            <span>{formatDateTime(detail.data.entry.time)}</span>
            <span>severity {severityLabel(detail.data.entry.severity)}</span>
            {detail.data.entry.userId && <span>user {detail.data.entry.userId}</span>}
            {detail.data.entry.machine && <span>machine {detail.data.entry.machine}</span>}
          </dl>
          <pre className="sx-pre" data-testid="error-payload">
            {JSON.stringify(detail.data.data, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
}

/** The occurrences of one group, with acknowledge/close per entry or for the whole group; an occurrence opens under its row. */
function GroupOccurrences({
  group,
  customerId,
  workserver,
  window,
  onChanged,
}: {
  group: ErrorGroup;
  customerId?: number;
  workserver?: string;
  window: { from: string; to: string };
  onChanged: () => void;
}) {
  const login = useHubLogin();
  const canAdmin = hasScope(login?.scopes, SCOPES.statsAdmin);
  const [status, setStatus] = useState<StatusFilter>('active');
  const [muting, setMuting] = useState(false);
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const list = useAsync(
    () =>
      stats.errors({ fingerprint: group.fingerprint, customerId, workserver, status, ...window, limit: 200 }),
    [group.fingerprint, customerId, workserver, status, window.from, window.to]
  );
  const bulk = useBulk(
    (ids, action) =>
      ids.length
        ? stats.errorBulk({ ids, action })
        : stats.errorBulk({ fingerprint: group.fingerprint, customerId, action }),
    () => {
      void list.reload();
      onChanged();
    }
  );
  const items = list.data?.items ?? [];
  const ids = [...bulk.selected];
  const scope = customerId ? 'of this organization' : 'of every organization';
  return (
    <>
      {bulk.toast.element}
      <p className="sx-msg">
        <b>{group.title}</b>
      </p>
      {group.sample && <pre className="sx-pre">{group.sample}</pre>}
      <dl className="sx-meta">
        <span>
          <b>{fmtInt(group.count)}</b> in range · <b>{fmtInt(group.open)}</b> open
        </span>
        <span>first seen {formatDateTime(group.firstSeen)}</span>
        <span>{group.organizations} organizations</span>
        <span className="nyk-mono">{group.fingerprint}</span>
      </dl>
      <div className="sx-toolbar">
        <Select
          aria-label="Status"
          value={status}
          onChange={(v) => setStatus(v as StatusFilter)}
          options={[
            { value: 'active', label: 'Open + acknowledged' },
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' },
            { value: 'all', label: 'All' },
          ]}
        />
        <span className="sx-spacer" />
        {canAdmin && ids.length > 0 && (
          <>
            <Button
              size="sm"
              variant="secondary"
              loading={bulk.busy}
              onClick={() => void bulk.run(ids, 'ack', 'acknowledged')}
            >
              Acknowledge {ids.length}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              loading={bulk.busy}
              onClick={() => void bulk.run(ids, 'close', 'closed')}
            >
              Close {ids.length}
            </Button>
          </>
        )}
        {canAdmin && !ids.length && (
          <>
            <ConfirmButton
              size="sm"
              variant="secondary"
              message={`Acknowledge every open entry of this group ${scope}?`}
              onConfirm={() => void bulk.run([], 'ack', 'acknowledged')}
            >
              Acknowledge group
            </ConfirmButton>
            <ConfirmButton
              size="sm"
              variant="secondary"
              message={`Close every entry of this group ${scope}? Closed entries move to history.`}
              onConfirm={() => void bulk.run([], 'ackclose', 'closed')}
            >
              Close group
            </ConfirmButton>
          </>
        )}
        {canAdmin && (
          <Button size="sm" variant="secondary" onClick={() => setMuting(true)}>
            Mute
          </Button>
        )}
      </div>
      {muting && (
        <MuteGroupDialog
          group={group}
          customerId={customerId}
          onClose={() => setMuting(false)}
          onDone={(message) => {
            bulk.toast.success(message);
            void list.reload();
            onChanged();
          }}
        />
      )}
      <ErrorBox error={list.error ? statsError(list.error) : null} />
      <Loading loading={list.loading && !list.data} />
      <TableScroller>
        <table className="nyk-ui-table nyk-cards" data-testid="occurrences" aria-label="Occurrences">
          <thead>
            <tr>
              {canAdmin && <th aria-label="Select" />}
              <th>Time</th>
              <th>Organization</th>
              <th>WorkServer</th>
              <th>User</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          {items.map((e) => (
            <tbody key={e.id} className="nyk-row-group">
              <tr>
                {canAdmin && (
                  <td>
                    {e.status !== 'closed' && (
                      <input
                        type="checkbox"
                        className="sx-check"
                        aria-label={`Select ${e.id}`}
                        checked={bulk.selected.has(e.id)}
                        onChange={() => bulk.toggle(e.id)}
                      />
                    )}
                  </td>
                )}
                <td className="nyk-card-title">{formatDateTime(e.time)}</td>
                <td data-label="Organization">
                  <OrgLink customerId={e.customerId} name={e.name} />
                </td>
                <td data-label="WorkServer">{e.workserver ?? '—'}</td>
                <td data-label="User">{e.userId ?? '—'}</td>
                <td data-label="Status">
                  <EntryStatusChip status={e.status} />
                </td>
                <td className="nyk-row-actions">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-expanded={opened.has(e.id)}
                    onClick={() => toggle(e.id)}
                  >
                    {opened.has(e.id) ? 'Hide details' : 'Details'}
                  </Button>
                </td>
              </tr>
              {opened.has(e.id) && (
                <tr className="sx-occurrence-row">
                  <td colSpan={canAdmin ? 7 : 6}>
                    <OccurrenceDetail id={e.id} />
                  </td>
                </tr>
              )}
            </tbody>
          ))}
        </table>
      </TableScroller>
      {list.data && !items.length && <EmptyState title="No entries with this status in range." />}
    </>
  );
}

/** Error groups (replaces PMS's alarm list): WorkServer error logs grouped by normalized message. */
export function ErrorGroupsPage() {
  const [customer] = useParam('customerId', '');
  const [workserver] = useParam('workserver', '');
  const [status, setStatus] = useParam<'active' | 'all'>('status', 'active');
  const [sort, setSort] = useParam<'last' | 'count'>('sort', 'last');
  const [fp] = useParam('fp', '');
  const [range, setRange] = useRange('7d');
  const { search } = useLocation();
  const w = useMemo(() => rangeWindow(range), [range]);
  const customerId = customer ? Number(customer) : undefined;
  const list = useAsync(
    () =>
      stats.errorGroups({
        customerId,
        workserver: workserver || undefined,
        status,
        sort,
        from: w.from,
        to: w.to,
        limit: 200,
      }),
    [customerId, workserver, status, sort, w.from, w.to]
  );
  const items = list.data?.items ?? [];
  // A group used to open in a drawer (`?fp=`); such links now go to its page.
  if (fp) return <Navigate replace to={groupPath(fp, search)} />;
  return (
    <Page
      title="Error groups"
      description={
        customerId
          ? `Errors of organization ${customerId}${workserver ? ` · ${workserver}` : ''}, grouped by normalized message.`
          : 'WorkServer error logs of every organization, grouped by normalized message (numbers, ids and times removed).'
      }
    >
      <div className="sx-toolbar">
        <RangeTabs value={range} onChange={setRange} max="30d" />
        <Select
          aria-label="Status"
          value={status}
          onChange={(v) => setStatus(v as 'active' | 'all')}
          options={[
            { value: 'active', label: 'With open entries' },
            { value: 'all', label: 'All' },
          ]}
        />
        <Select
          aria-label="Sort"
          value={sort}
          onChange={(v) => setSort(v as 'last' | 'count')}
          options={[
            { value: 'last', label: 'Last seen' },
            { value: 'count', label: 'Most entries' },
          ]}
        />
      </div>
      <ErrorBox error={list.error ? statsError(list.error) : null} />
      <Loading loading={list.loading && !list.data} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="groups-table">
            <thead>
              <tr>
                <th>Error</th>
                <th className="sx-num">Entries</th>
                <th className="sx-num">Open</th>
                <th>Trend</th>
                <th>Where</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.fingerprint} data-fp={g.fingerprint}>
                  <td className="nyk-card-title sx-msg">
                    <Link className="nyk-link sx-msg-clip" to={groupPath(g.fingerprint, search)}>
                      {g.title}
                    </Link>
                    <div className="nyk-ui-hint">
                      {g.new ? 'new · ' : ''}first seen {formatRelative(g.firstSeen)}
                    </div>
                  </td>
                  <td data-label="Entries" className="sx-num">
                    {fmtInt(g.count)}
                  </td>
                  <td data-label="Open" className="sx-num">
                    {fmtInt(g.open)}
                  </td>
                  <td data-label="Trend">
                    <Sparkline values={g.trend.map((t) => t.count)} label={`${g.count} entries`} />
                  </td>
                  <td data-label="Where" className="nyk-wrap">
                    {g.installations
                      .slice(0, 3)
                      .map((i) => `${i.name ?? i.customerId} · ${i.workserver ?? '?'}`)
                      .join(', ')}
                    {g.installations.length > 3 && ` +${g.installations.length - 3}`}
                  </td>
                  <td data-label="Last seen">{formatRelative(g.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {list.data && !items.length && <EmptyState title="No error groups in this range." />}
        {list.data?.hasMore && <p className="nyk-ui-hint">Showing 200 groups; narrow the range or filter.</p>}
      </div>
    </Page>
  );
}

/** A group's page, keeping the list's filters (organization, WorkServer, range) in the query. */
export function groupPath(fingerprint: string, search = ''): string {
  const q = new URLSearchParams(search);
  q.delete('fp');
  const rest = q.toString();
  return `/statistics/errors/${encodeURIComponent(fingerprint)}${rest ? `?${rest}` : ''}`;
}

/** One error group: what it is, where it happens and its occurrences (`/statistics/errors/:fingerprint`). */
export function ErrorGroupPage() {
  const { fingerprint = '' } = useParams();
  const { search } = useLocation();
  const [customer] = useParam('customerId', '');
  const [workserver] = useParam('workserver', '');
  const [range, setRange] = useRange('7d');
  const w = useMemo(() => rangeWindow(range), [range]);
  const customerId = customer ? Number(customer) : undefined;
  // The group's summary comes from the group list with the same filters (the collector has no single-group read).
  const groups = useAsync(
    () =>
      stats.errorGroups({
        customerId,
        workserver: workserver || undefined,
        status: 'all',
        sort: 'last',
        from: w.from,
        to: w.to,
        limit: 200,
      }),
    [customerId, workserver, w.from, w.to]
  );
  const group = groups.data?.items.find((g) => g.fingerprint === fingerprint);
  const listPath = `/statistics/errors${search}`;
  return (
    <Page
      title={
        <>
          <Link to={listPath} className="nyk-back">
            Error groups
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          Error group
        </>
      }
      description={
        customerId
          ? `Organization ${customerId}${workserver ? ` · ${workserver}` : ''}`
          : 'Every organization'
      }
    >
      <div className="sx-toolbar">
        <RangeTabs value={range} onChange={setRange} max="30d" />
      </div>
      <ErrorBox error={groups.error ? statsError(groups.error) : null} />
      <Loading loading={groups.loading && !groups.data} />
      {groups.data && !group && (
        <EmptyState
          card
          title="This error group has no entries in the range."
          hint="Choose a longer range, or open the group from the list."
        />
      )}
      {group && (
        <div className="nyk-card" data-testid="error-group">
          <GroupOccurrences
            key={group.fingerprint}
            group={group}
            customerId={customerId}
            workserver={workserver || undefined}
            window={{ from: w.from, to: w.to }}
            onChanged={() => void groups.reload()}
          />
        </div>
      )}
    </Page>
  );
}
